import { format } from "date-fns"
import { CircleCheck } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import styles from "./gestation-calendar-accordion.module.css"
import AddEventIcon from "@/assets/icons/misc/add-event-icon.tsx"
import ArrowDown from "@/assets/icons/misc/arrow-down.tsx"
import EmptyCalendarIcon from "@/assets/icons/misc/empty-calendar-icon.tsx"
import { Button } from "@/components/shadcn/button.tsx"
import {
  ANIMAL_EVENT_LIST_VIEW,
  NEW_ANIMAL_EVENT_VIEW,
} from "@/features/events/animal-events/routes/route-list.ts"
import GestationCalendarActionButton from "@/features/gestational-calendar/components/gestation-calendar-action-button/gestation-calendar-action-button.tsx"
import { GestationCard } from "@/features/gestational-calendar/components/gestation-card/gestation-card.tsx"
import { useNavigateBirthEvent } from "@/features/gestational-calendar/hooks/use-navigate-birth-event.ts"
import {
  CalendarActivityState,
  type CalendarActivity,
  type GestationCalendar,
} from "@/features/gestational-calendar/types/calendar-types.ts"
import { FP_QUERY_PARAMS } from "@/features/gestational-calendar/utils/gestation-calendar-consts.ts"
import {
  checkIfTodayOrIfInInterval,
  formatGestationalCalendarInterval,
} from "@/features/gestational-calendar/utils/gestational-calendar-utils.ts"
import { cn } from "@/utils/styles.ts"

interface AccordionProps {
  gestationCalendar: GestationCalendar
  activityState: CalendarActivityState
  calendarActivity: CalendarActivity
  isCalvingDone?: boolean
  isAfterCalving?: boolean
  animalId: string
  isCalving?: boolean
  isOpen?: boolean
  showActionButtons?: boolean
}

const GestationCalendarAccordion: React.FC<AccordionProps> = ({
  gestationCalendar,
  activityState,
  calendarActivity,
  animalId,
  isAfterCalving,
  isCalvingDone = false,
  isCalving,
  isOpen = false,
  showActionButtons = true,
}) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(isOpen)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isCurrent = checkIfTodayOrIfInInterval(calendarActivity)
  const navigateToBirthEvent = useNavigateBirthEvent()
  const handleAccordionOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    setIsAccordionOpen((prevState) => !prevState)
  }

  const handleAddEvent = () => {
    const searchParams = new URLSearchParams()
    searchParams.append(FP_QUERY_PARAMS.ANIMAL_ID, animalId)
    searchParams.append(
      FP_QUERY_PARAMS.GESTATION_CALENDAR_ENTRY_ID,
      calendarActivity.id,
    )
    searchParams.append(
      FP_QUERY_PARAMS.GESTATION_CALENDAR_ID,
      gestationCalendar.id,
    )
    if (calendarActivity.event.animal_event_type) {
      searchParams.append(
        FP_QUERY_PARAMS.EVENT_TYPE_ID,
        calendarActivity.event.animal_event_type,
      )
    }

    navigate(`${NEW_ANIMAL_EVENT_VIEW}?${searchParams.toString()}`)
  }

  const handleViewEvent = (eventId: string) => {
    navigate(`${ANIMAL_EVENT_LIST_VIEW}/${eventId}`)
  }

  const dateToShow = useMemo(
    () => formatGestationalCalendarInterval(calendarActivity),
    [calendarActivity],
  )

  const displayedDate = () => {
    if (
      isCalvingDone &&
      isCalving &&
      calendarActivity.event.is_calving_relative &&
      calendarActivity.event.time_offset === 0
    ) {
      return format(gestationCalendar.calving_date, "dd.MM.yyyy HH:mm")
    }
    return dateToShow
  }

  const showGestationCard =
    !gestationCalendar.abortion_confirmed &&
    isCurrent &&
    calendarActivity.event.metadata?.is_gestation_confirmation_event &&
    !calendarActivity.animal_event

  const showGeneralAddCard =
    !gestationCalendar.abortion_confirmed &&
    calendarActivity.event.animal_event_type &&
    isCurrent &&
    !isCalving &&
    !calendarActivity.animal_event &&
    !calendarActivity.event.metadata?.is_gestation_confirmation_event

  const showCalvingAddCard =
    !gestationCalendar.abortion_confirmed &&
    isCurrent &&
    isCalving &&
    !calendarActivity.animal_event &&
    !calendarActivity.event.metadata?.is_gestation_confirmation_event

  return (
    <div
      className={cn(
        "w-full rounded-lg border border-[var(--grey)] bg-white p-1",
        activityState === CalendarActivityState.ABORTED && "border-red-500",
        activityState === CalendarActivityState.DONE &&
          !isAfterCalving &&
          "border-none bg-[var(--transparent-brown)]",
        activityState === CalendarActivityState.DONE &&
          isAfterCalving &&
          "border-none bg-[var(--transparent-purple)]",
        activityState === CalendarActivityState.CURRENT &&
          "border-[var(--light-purple)]",
      )}
      onClick={(event) => handleAccordionOpen(event)}
    >
      <div className={styles.accordionTrigger}>
        <div className={styles.triggerContent}>
          <div className={styles.title}>{calendarActivity.event.name}</div>
          <div className={styles.date}>{displayedDate()}</div>
        </div>
        <div className={styles.arrowWrapper} data-is-opened={isAccordionOpen}>
          <ArrowDown />
        </div>
      </div>
      <div
        className={styles.accordionContentWrapper}
        data-is-opened={isAccordionOpen}
      >
        <div className={styles.accordionContent}>
          {calendarActivity.event.description}
          {showActionButtons && (
            <>
              {showGestationCard && (
                <GestationCard
                  gestationCalendar={gestationCalendar}
                  calendarActivity={calendarActivity}
                />
              )}
              <div className={"flex items-center justify-center"}>
                {showGeneralAddCard && (
                  <GestationCalendarActionButton
                    text={t("addEvent")}
                    icon={<AddEventIcon />}
                    onClick={handleAddEvent}
                  />
                )}
                {calendarActivity.animal_event && (
                  <GestationCalendarActionButton
                    text={t("viewEvent")}
                    icon={<EmptyCalendarIcon size={17} />}
                    onClick={() =>
                      handleViewEvent(calendarActivity.animal_event)
                    }
                  />
                )}
                {showCalvingAddCard && (
                  <Button
                    size={"lg"}
                    className={"w-full gap-2 font-medium text-white"}
                    onClick={() => navigateToBirthEvent(gestationCalendar)}
                  >
                    <CircleCheck className={"text-white"} />
                    {t("confirmCalving")}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default GestationCalendarAccordion
